import { useEffect, useRef } from "react";
import { fireProductSummaryViewed } from "../../../utils/marketingTracking/marketingTrackingEvents";
import { markProductSummaryViewed } from "../../../utils/marketingTracking/productSummaryTrackingState";

const MINIMUM_QUALIFIED_VIEW_MS = 1000;
const MINIMUM_INTERSECTION_RATIO = 0.5;

type UseReturnNotesSummaryImpressionTrackingArgs = {
  isEnabled: boolean;
  productId?: number | string;
  productSummaryVersion?: number;
  fitScalePresent: boolean;
  fitAnalyticsPresent: boolean;
};

const useReturnNotesSummaryImpressionTracking = ({
  isEnabled,
  productId,
  productSummaryVersion,
  fitScalePresent,
  fitAnalyticsPresent,
}: UseReturnNotesSummaryImpressionTrackingArgs) => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const currentProperties = useRef({
    fitScalePresent,
    fitAnalyticsPresent,
  });
  currentProperties.current = { fitScalePresent, fitAnalyticsPresent };

  useEffect(() => {
    const section = sectionRef.current;
    if (
      !isEnabled ||
      productId === null ||
      productId === undefined ||
      productSummaryVersion === null ||
      productSummaryVersion === undefined ||
      !section ||
      typeof IntersectionObserver === "undefined"
    ) {
      return undefined;
    }

    let intersectionRatio = 0;
    let activeSpanStart: number | null = null;
    let qualificationTimer: ReturnType<typeof setTimeout> | undefined;
    let hasQualified = false;
    let hasFinalized = false;
    let totalMs = 0;
    let longestMs = 0;
    let spans = 0;
    let isHidden = document.visibilityState === "hidden";

    const now = () => performance.now();

    const clearQualificationTimer = () => {
      if (qualificationTimer !== undefined) {
        clearTimeout(qualificationTimer);
        qualificationTimer = undefined;
      }
    };

    const startSpan = () => {
      if (
        activeSpanStart !== null ||
        isHidden ||
        intersectionRatio < MINIMUM_INTERSECTION_RATIO
      ) {
        return;
      }

      activeSpanStart = now();
      qualificationTimer = setTimeout(() => {
        hasQualified = true;
        markProductSummaryViewed(productId);
      }, MINIMUM_QUALIFIED_VIEW_MS);
    };

    const stopSpan = () => {
      if (activeSpanStart === null) {
        return;
      }

      clearQualificationTimer();
      const duration = Math.max(0, now() - activeSpanStart);
      totalMs += duration;
      longestMs = Math.max(longestMs, duration);
      spans += 1;
      if (duration >= MINIMUM_QUALIFIED_VIEW_MS) {
        hasQualified = true;
        markProductSummaryViewed(productId);
      }
      activeSpanStart = null;
    };

    const finalize = () => {
      if (hasFinalized) {
        return;
      }

      stopSpan();
      hasFinalized = true;
      if (!hasQualified) {
        return;
      }

      const endReason =
        document.visibilityState === "hidden"
          ? "app_backgrounded"
          : intersectionRatio >= MINIMUM_INTERSECTION_RATIO
            ? "screen_dismissed"
            : "span_closed";

      fireProductSummaryViewed({
        product: { id: productId },
        productSummaryVersion,
        longestMs,
        totalMs,
        spans,
        endReason,
        fitScalePresent: currentProperties.current.fitScalePresent,
        fitAnalyticsPresent: currentProperties.current.fitAnalyticsPresent,
      });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        intersectionRatio = entry?.isIntersecting ? entry.intersectionRatio : 0;

        if (intersectionRatio >= MINIMUM_INTERSECTION_RATIO && !isHidden) {
          startSpan();
        } else {
          stopSpan();
        }
      },
      { threshold: [0, MINIMUM_INTERSECTION_RATIO] },
    );

    const handleVisibilityChange = () => {
      isHidden = document.visibilityState === "hidden";
      if (isHidden) {
        stopSpan();
      } else {
        startSpan();
      }
    };

    observer.observe(section);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", finalize);

    return () => {
      finalize();
      observer.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", finalize);
    };
  }, [isEnabled, productId, productSummaryVersion]);

  return sectionRef;
};

export default useReturnNotesSummaryImpressionTracking;
